import type { Case, CaseState, RecommendedAction } from '@/versions/v1/schema/case';
import type { PlantRoleId, RoleTable } from '@/versions/v1/schema/plant-role';
import type { ReportChannel, ReportEvaluatedAgainst, ReportTemplate } from '@/versions/v1/schema/report';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { assigneeLabel, STATE_CHIP } from '@/versions/v1/modules/advisory/Scene.transform';
import { rulingStateLabel } from '@/versions/v1/modules/advisory/Approvals.transform';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import { juneMtdTonnes, juneWtdDays, juneWtdTonnes, type JuneTonnageRead } from '@/versions/v1/shared/june-tonnage.derive';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import { DESIGN_THROUGHPUT_STPD } from '@/versions/v1/shared/bunker-hill-planning-seed';
import { effectivenessForAction } from '@/versions/v1/shared/EffectivenessEngine';
import { FMEA_UPDATE_REVIEWS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { seatLabelById } from '@/versions/v1/shared/org';
import {
  applyRecommendedActions,
  effectiveReportPinnedCaseIds,
  type AdvisorySessionState,
} from '@/versions/v1/shared/AdvisoryEngine';
import {
  displayCaseTitle,
  znDaysBelowDesign,
  znRecoveryExecutiveBullet,
} from '@/versions/v1/shared/recovery-vocab.derive';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import {
  ALARMS,
  CASE_OUTCOMES,
  CASES,
  KPI_DEFINITIONS,
  KPI_VALUES,
  RECOMMENDED_ACTIONS,
  TARGETS,
} from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { FORECASTS } from '@/versions/v1/shared/bunker-hill-planning-seed';
import {
  ACCOUNTING_REPORT,
  RECONCILIATION_CHECK,
} from '@/versions/v1/shared/bunker-hill-accounting-seed';
import {
  allClosureReads,
  closureRead,
  formatGapPct,
  recoveryActualPct,
} from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import { periodView } from '@/versions/v1/modules/ingestion/periodBalance';
import {
  CONTROL_LOOPS,
  EXTRA_ROLE_TABLES,
  REPORT_DAILY,
  REPORT_INSTANCES,
  REPORT_TEMPLATES,
  ROLE_KPIS,
  ROLE_TABLES,
  SAMPLE_POINTS,
  SUBPROCESSES,
} from '@/versions/v1/shared/bunker-hill-seed';
import {
  asCaseTime,
  diffMinutes,
  formatClock,
  formatClockShort,
  formatElapsedMinutes,
  parseTime,
  weekStart,
} from '@/versions/v1/shared/clock.derive';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import {
  BASIS_LABEL,
  resolveComparisonBasis,
} from '@/versions/v1/shared/comparison-basis';
import { applyCases } from '@/versions/v1/shared/ScenarioEngine';
import { formatValue, readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { plantPhrase } from '@/versions/v1/shared/project-seed';
import {
  formatMoneyK,
  formatMoneyPerT,
  nsrRead,
  NSR_SYN_PROVENANCE,
  operatingCostRead,
  recoveryShortfallCost,
} from '@/versions/v1/shared/NsrEngine';
import { reportTemplateCarriesMoney } from '@/versions/v1/shared/money-access';

/** Financial snapshot rows — every figure from NsrEngine over the session state (D-money). */
export function financialSnapshot(state: SessionState): {
  basisLabel: string;
  basisProvenance: string;
  rows: readonly DailyFinancialRow[];
} {
  const nsr = nsrRead(state);
  const cost = operatingCostRead(state);
  const shortfall = recoveryShortfallCost(state);
  return {
    basisLabel: 'Budget',
    basisProvenance: NSR_SYN_PROVENANCE,
    rows: [
      { label: 'NSR today', value: `${formatMoneyK(nsr.daily.total)} · target ${formatMoneyK(nsr.budgetDaily)}` },
      { label: 'NSR month-to-date', value: formatMoneyK(nsr.mtd) },
      { label: 'Operating cost per tonne', value: `${formatMoneyPerT(cost.actualPerT)} · target ${formatMoneyPerT(cost.targetPerT)}` },
      { label: 'Recovery shortfall cost', value: `${formatMoneyK(shortfall.mtdTotal)} month-to-date` },
    ],
  };
}

export type ReportsTab = 'daily-management' | 'daily-accounting' | 'weekly' | 'shift-report';

export type ReportsCopy = {
  title: string;
  description: string;
};

export type AdvisoryStateCount = {
  state: CaseState;
  label: string;
  count: number;
};

export type TemplateRegistryRow = {
  tabId: ReportsTab;
  templateId: string;
  title: string;
  audience: string;
  cadence: string;
  channel: string;
  lastInstance: string;
  workflowChip: string;
  workflowChipTitle: string;
  evaluatedAgainstChip: string;
};

export type DailyLabelValueRow = {
  label: string;
  value: string;
  muted?: boolean;
};

export type DailyEnvironmentalRow = {
  label: string;
  value: string;
  limit: string;
  stateLabel: string;
  stateTone: 'success' | 'warning' | 'danger';
  provenance?: string;
};

export type DailyFinancialRow = {
  label: string;
  value: string;
};

export type DailyDeckFigure = {
  label: string;
  value: string;
  basis: string;
  provenance: string;
};

export type DailyRecoveryRow = {
  metal: string;
  actual: string;
  basisLabel: string;
  target: string;
  designSecondary?: string;
  aiInsight?: string;
};

export type DailyRecommendedActionRow = {
  actionId: string;
  text: string;
  urgency: string;
  disposition: string;
  fmeaReviewId?: string;
  fmeaStageLabel?: string;
  undecided: boolean;
};

export type DailyReportEdition = {
  id: 'sent' | 'current';
  label: string;
  cutoff: SeriesTime;
  editBeforeSend: boolean;
  editDisabledReason?: string;
};

// route-reads paul-feedback.A8 slice 3 (#268) — the decision record, grouped
// by the plant's own shift. A recommendation's shift is the shift its case
// was raised in (`Case.raisedAt`, plant-clock, never the decision time) —
// the shift is a fact about when the AI made the recommendation, fixed at
// the moment it fired, not about when someone got to it. `secondary: true`
// marks the two `sequence: null` per-case rows: this stream includes them
// here (unlike the merged numbered list above) because brief slice 3 point 5
// calls them "recommendations the AI made" and this is the record of
// recommendations the AI made — the sequenced list stays the narrower,
// separate "plant-wide numbered" view it already was.
export type DailyShiftActionRow = {
  actionId: string;
  caseId: string;
  text: string;
  urgency: string;
  secondary: boolean;
  /** `Case.assignedTo`, reached through `caseId` — never a copy on the action
   *  (paul-feedback.A8 hard rule 4 / #279 hard rule 5). */
  recipientLabel: string;
  /** "awaiting decision" | "approved" | "rejected" | "implemented" — an
   *  undecided row always renders this label, never a blank and never
   *  "rejected" (common rule 11 / brief slice 3 point 3). */
  statusLabel: string;
  deciderLabel?: string;
  /** Refusal reason — present only when `statusLabel` is "rejected". */
  reason?: string;
  /** raised → decided. Always a rendered word or figure, never a bare
   *  placeholder standing in for three different absences (SHOULD-FIX 1,
   *  fixer round after the uptake review): `"—"` when undecided (nothing to
   *  measure yet), `"not measurable"` when a decision stamp predates its own
   *  case's `raisedAt` (a seed-clock contradiction, not a small number), or
   *  the formatted elapsed time. */
  raisedToDecidedElapsed: string;
  /** decided → implemented — a different quantity from the one above, never
   *  summed with it or substituted for it (hard rule 3). `"—"` when
   *  undecided or rejected (implementation does not apply), `"not yet
   *  implemented"` when approved and still sitting, `"not measurable"` on
   *  the same clock-contradiction guard, or the formatted elapsed time. */
  decidedToImplementedElapsed: string;
  fmeaReviewId?: string;
  fmeaStageLabel?: string;
};

export type DailyShiftActionGroup = {
  roleId: PlantRoleId;
  roleLabel: string;
  rows: readonly DailyShiftActionRow[];
};

export type DailyGuidedLink = {
  label: string;
  href: string;
  tab?: ReportsTab;
};

export type DailyManagementReportView = {
  workflowTabChip: string;
  workflowTabChipTitle: string;
  workflowMeta: string;
  workflowProvenanceLine: string;
  evaluatedAgainstChip: string;
  comparisonBasisLabel: string | null;
  executiveBullets: readonly DailyExecutiveBullet[];
  trackedIssues: readonly DailyTrackedIssueView[];
  aiSummary: string;
  productionRows: readonly DailyLabelValueRow[];
  recoveryRows: readonly DailyRecoveryRow[];
  impurityRows: readonly DailyLabelValueRow[];
  financialBasisChip: string;
  financialBasisProvenance: string;
  financialRows: readonly DailyFinancialRow[];
  availability: string;
  emergingRisks: readonly string[];
  environmentalRows: readonly DailyEnvironmentalRow[];
  forecastingLines: readonly DailyLabelValueRow[];
  forecastingProvenance: DailyGuidedLink;
  recommendedActions: readonly DailyRecommendedActionRow[];
  shiftActionGroups: readonly DailyShiftActionGroup[];
  guidedLinks: readonly DailyGuidedLink[];
  editions: readonly DailyReportEdition[];
  activeEditionId: 'sent' | 'current';
  decisionWindowLabel: string;
  /** When true, the report body renders one section at a time (vision.V9 #313). */
  paginateBySection: boolean;
};

const ALL_TABLES: readonly RoleTable[] = [...ROLE_TABLES, ...EXTRA_ROLE_TABLES];

const TAB_BY_TEMPLATE: Record<string, ReportsTab> = {
  'tpl-daily-management': 'daily-management',
  'tpl-daily-accounting': 'daily-accounting',
  'tpl-weekly-summary': 'weekly',
  'tpl-met-shift': 'shift-report',
};

const TEMPLATE_BY_TAB: Record<ReportsTab, string> = {
  'daily-management': 'tpl-daily-management',
  'daily-accounting': 'tpl-daily-accounting',
  weekly: 'tpl-weekly-summary',
  'shift-report': 'tpl-met-shift',
};

/** The Reports tab that renders a template (the Workflows page links a report run to it). */
export function reportsTabForTemplate(templateId: string): ReportsTab | null {
  const match = (Object.keys(TEMPLATE_BY_TAB) as ReportsTab[]).find((tab) => TEMPLATE_BY_TAB[tab] === templateId);
  return match ?? null;
}

/** `?tab=` → a tab this reader can see, else the default tab. */
export function reportsTabFromParam(param: string | null, visible: readonly { id: ReportsTab }[]): ReportsTab {
  const match = visible.find((tab) => tab.id === param);
  return match?.id ?? 'daily-management';
}

const RECOVERY_CASE_IDS = new Set(['case-zn-recovery', 'case-ag-grade']);
const EMERGING_RISK_CASE_IDS = new Set(['case-p80', 'case-as-trend']);

export type DailyAccountingReportView = {
  /**
   * The one card-level chip: a decode for the rows drawn in muted ink, naming
   * the design-basis constants that sit beside the day's engine output. Not a
   * sentence about the card — three provenance sentences said the same thing
   * twice and one narrated the transform to a GM (r11 SHOULD-FIX 1/2).
   */
  designBasisScope: string;
  /** Source cite for the seed rows above — the `ProvenanceChip` tooltip, the
   *  traceability record that stays off the glass (DESIGN.md § Decided). */
  provenance: string;
  productionSummary: readonly DailyLabelValueRow[];
  balanceByNode: readonly DailyLabelValueRow[];
  reconciliationAdjustments: readonly DailyLabelValueRow[];
  inventory: readonly DailyLabelValueRow[];
  signOff: string;
};

export const REPORTS_TABS: { id: ReportsTab; label: string }[] = [
  { id: 'daily-management', label: 'Daily management report' },
  { id: 'daily-accounting', label: 'Daily accounting report' },
  { id: 'weekly', label: 'Weekly summary' },
  { id: 'shift-report', label: 'Met shift report' },
];

export function visibleReportsTabs(showMoney = true): { id: ReportsTab; label: string }[] {
  return REPORTS_TABS.filter((tab) => {
    const template = reportTemplate(tab.id);
    if (!template) return true;
    return showMoney || !reportTemplateCarriesMoney(template);
  });
}

export type DailyReportSectionNavItem = { id: string; label: string };

/** Anchor targets for the daily report's nine C3 sections, in document order. */
export const DAILY_REPORT_SECTION_NAV: DailyReportSectionNavItem[] = [
  { id: 'daily-report-tracked-issue', label: 'Tracked issue' },
  { id: 'daily-report-executive-summary', label: 'Summary' },
  { id: 'daily-report-production', label: 'Production' },
  { id: 'daily-report-recovery', label: 'Recovery & grade' },
  { id: 'daily-report-impurities', label: 'Impurities' },
  { id: 'daily-report-financial', label: 'Financial' },
  { id: 'daily-report-stability', label: 'Stability' },
  { id: 'daily-report-environmental', label: 'Environmental' },
  { id: 'daily-report-forecasting', label: 'Forecasting' },
  { id: 'daily-report-actions', label: 'Actions' },
];

export type DailyExecutiveBullet = {
  text: string;
  sectionId?: string;
};

export type DailyTrackedIssueView = {
  caseId: string;
  title: string;
  recoveryLine: string;
  latestDecision: string;
  drawerHref: string;
};

export function reportsCopy(plantName: string): ReportsCopy {
  return {
    title: 'Reports',
    description: `Template registry and target-basis rollups for ${plantPhrase(plantName)} — daily management email, daily accounting report, GM weekly summary, and Met shift report.`,
  };
}

function formatChannel(channel: ReportChannel): string {
  return channel === 'in-app' ? 'In-app' : 'Email';
}

function environmentalComplianceRows(): DailyEnvironmentalRow[] {
  const tsf = readingByIdLookup('rd-env-tsf-freeboard');
  const water = readingByIdLookup('rd-env-water-balance');
  const ph = readingByIdLookup('rd-env-discharge-ph');
  return [
    {
      label: 'TSF freeboard',
      value: tsf ? formatValue(tsf, { withUnit: true }) : '2.4 m',
      limit: '≥ 2.0 m',
      stateLabel: 'in band',
      stateTone: 'success',
      provenance: '[syn] Illustrative permit TSF-01 freeboard minimum',
    },
    {
      label: 'Process water balance',
      value: water ? formatValue(water, { withUnit: true }) : '+120 m³/d net',
      limit: '± 200 m³/d',
      stateLabel: 'in band',
      stateTone: 'success',
      provenance: '[syn] Site water balance — illustrative permit ENV-08',
    },
    {
      label: 'Discharge pH compliance',
      value: ph ? formatValue(ph, { withUnit: false }) : '28 of 28 samples',
      limit: 'permit pH 6.5–8.5',
      stateLabel: 'compliant',
      stateTone: 'success',
      provenance: '[syn] Permit ENV-12 discharge condition count',
    },
  ];
}

const ACTION_STATUS_LABEL: Record<string, string> = {
  proposed: 'awaiting decision',
  approved: 'approved',
  rejected: 'rejected',
  done: 'implemented',
};

function elapsedReading(elapsed: number | undefined, notApplicableLabel: string): string {
  if (elapsed === undefined) return notApplicableLabel;
  if (elapsed < 0) return '—';
  return formatElapsedMinutes(elapsed);
}

function fmeaLinkForCase(caseId: string, cursor: SeriesTime): { reviewId: string; stageLabel: string } | undefined {
  const outcome = CASE_OUTCOMES.find((row) => row.caseId === caseId);
  if (!outcome) return undefined;
  const review = FMEA_UPDATE_REVIEWS.find((row) => row.caseOutcomeId === outcome.id);
  if (!review) return undefined;
  return { reviewId: review.id, stageLabel: rulingStateLabel(review, cursor) };
}

function decisionAtOrBeforeCutoff(decisionAt: SeriesTime, cutoff: SeriesTime): boolean {
  return parseTime(decisionAt).getTime() <= parseTime(cutoff).getTime();
}

function shiftActionRow(
  action: RecommendedAction,
  caseItem: Case,
  cursor: SeriesTime,
): DailyShiftActionRow {
  const raisedFull = asCaseTime(caseItem.raisedAt);
  const decidedFull = action.decision ? asCaseTime(action.decision.at) : undefined;
  const implementedFull = action.implementedAt ? asCaseTime(action.implementedAt) : undefined;
  const raisedToDecided = decidedFull ? diffMinutes(decidedFull, raisedFull) : undefined;
  const decidedToImplemented =
    decidedFull && implementedFull ? diffMinutes(implementedFull, decidedFull) : undefined;
  return {
    actionId: action.id,
    caseId: action.caseId,
    text: action.text,
    urgency: action.urgency,
    secondary: action.sequence === null,
    recipientLabel: assigneeLabel(caseItem.assignedTo),
    statusLabel: ACTION_STATUS_LABEL[action.status] ?? action.status,
    deciderLabel: action.decision ? seatLabelById(action.decision.seat) : undefined,
    reason: action.decision?.reason,
    // Undecided → "—" (nothing to measure yet); a decision stamp that
    // predates its own case's raisedAt (ra-1, recorded known seed
    // limitation — docs/limitation.md) → "not measurable", never a
    // misleading 0m or the same dash an undecided row wears.
    raisedToDecidedElapsed: elapsedReading(raisedToDecided, '—'),
    // Approved-and-not-yet-implemented reads its own word, not the dash a
    // rejected or undecided row wears for the same column — those two never
    // reach implementation at all, so "—" stays honest for them.
    decidedToImplementedElapsed: elapsedReading(
      decidedToImplemented,
      action.status === 'approved' ? 'not yet implemented' : '—',
    ),
    ...(() => {
      const fmea = fmeaLinkForCase(action.caseId, cursor);
      return fmea
        ? { fmeaReviewId: fmea.reviewId, fmeaStageLabel: fmea.stageLabel }
        : {};
    })(),
  };
}

const DECISION_RECORD_ROLES: readonly PlantRoleId[] = [
  'cro',
  'metallurgist',
  'maintenance',
  'ops-supervisor',
  'operator',
];

function decisionWithinTrailing24h(decisionAt: SeriesTime, cursor: SeriesTime): boolean {
  const cursorMs = parseTime(cursor).getTime();
  const decidedMs = parseTime(decisionAt).getTime();
  const windowStart = cursorMs - 24 * 60 * 60 * 1000;
  return decidedMs >= windowStart && decidedMs <= cursorMs;
}

/** Trailing-24 h decision record grouped by executing role (vision.V9 #313).
 *  Reads through `applyRecommendedActions` so a same-session drawer decision
 *  reaches this table without a page load. */
export function dailyShiftActionGroups(
  state: SessionState,
  advisory: AdvisorySessionState,
  editionCutoff: SeriesTime = state.cursor,
): DailyShiftActionGroup[] {
  const actions = applyRecommendedActions(RECOMMENDED_ACTIONS, advisory);
  const rowsByRole = new Map<PlantRoleId, DailyShiftActionRow[]>();

  for (const action of actions) {
    if (!action.decision) continue;
    const decidedFull = asCaseTime(action.decision.at);
    if (!decisionAtOrBeforeCutoff(decidedFull, editionCutoff)) continue;
    if (!decisionWithinTrailing24h(decidedFull, editionCutoff)) continue;
    const caseItem = CASES.find((row) => row.id === action.caseId);
    if (!caseItem) continue;
    const role = caseItem.assignedTo;
    if (!DECISION_RECORD_ROLES.includes(role)) continue;
    const bucket = rowsByRole.get(role) ?? [];
    bucket.push(shiftActionRow(action, caseItem, editionCutoff));
    rowsByRole.set(role, bucket);
  }

  return DECISION_RECORD_ROLES.map((roleId) => ({
    roleId,
    roleLabel: assigneeLabel(roleId),
    rows: rowsByRole.get(roleId) ?? [],
  })).filter((group) => group.rows.length > 0);
}

// The throughput/recovery KPIs this report's single "comparison basis" chip
// describes. Concentrate grade (kpi-zn-grade, kpi-ag-grade) is deliberately
// excluded: the FD gives no ramp/budget grade for concentrate (pl.D1 round
// 3, #142; ruling A), those two always resolve to 'design' now, and the
// report already states that in its own words ("vs design", line ~454) —
// including them here would silently blank the chip whenever a headline
// Budget basis and a concentrate Design basis disagree, which is every day.
const DAILY_REPORT_KPI_IDS = [
  'kpi-ore-tpd',
  'kpi-zn-rec',
  'kpi-pb-rec',
] as const;

function resolvePageComparisonBasis(kpiIds: readonly string[]): string | null {
  const labels = kpiIds.map(
    (id) => resolveComparisonBasis(id, '24h', TARGETS)?.label ?? BASIS_LABEL.budget,
  );
  const unique = [...new Set(labels)];
  return unique.length === 1 ? unique[0]! : null;
}

function kpiTriple(kpiId: string): {
  actual?: number;
  // The target actually being compared against, whichever basis won the
  // ranking (pl.D1 round 2, #142) — never a hardcoded 'ramp' lookup; reads
  // `basisRead.value` directly so it stays correct as the ranking evolves.
  basisTarget?: number;
  designTarget?: number;
  unit: string;
  deltaVsTarget?: string;
  deltaVsDesign?: string;
  basisLabel?: string;
  basisValue?: number;
  designSecondary?: string;
} {
  const def = KPI_DEFINITIONS.find((row) => row.id === kpiId);
  const value = KPI_VALUES.find((row) => row.kpiId === kpiId);
  const basisRead = resolveComparisonBasis(kpiId, '24h', TARGETS);
  const designTarget = TARGETS.find((row) => row.kpiId === kpiId && row.basis === 'design');
  const designSecondary =
    basisRead && basisRead.design !== undefined && basisRead.basis !== 'design'
      ? `${basisRead.design.toFixed(1)}${def?.unit === '%' ? '%' : ''}`
      : undefined;
  return {
    actual: value?.value,
    basisTarget: basisRead?.value,
    designTarget: designTarget?.value ?? basisRead?.design,
    unit: def?.unit ?? '',
    deltaVsTarget: value?.deltaVsTarget,
    deltaVsDesign: value?.deltaVsDesign,
    basisLabel: basisRead?.label,
    basisValue: basisRead?.value,
    designSecondary,
  };
}

export function formatEvaluatedAgainstChip(evaluated: ReportEvaluatedAgainst): string {
  return `model v${evaluated.modelVersion} · rules v${evaluated.ruleSetVersion}`;
}

function templateShortName(template: ReportTemplate): string {
  return template.title.replace(/\s+report$/i, '');
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatTonnes(value: number): string {
  return `${value.toLocaleString('en-US')} t/d`;
}

function formatDst(value: number): string {
  return `${value.toLocaleString('en-US')} dst`;
}

function oreThroughputRollups(
  wtd: JuneTonnageRead,
  mtd: JuneTonnageRead,
): { wtd: string; mtd: string } {
  // D25 / K21 round 2 (#719): week-to-date and month-to-date are the one shared
  // June-to-date reads — the plant week's complete production days (2–7 Jun, 6
  // days, 10,652 dst) and the cursor month's (1–7 Jun, 7 days, 12,401 dst) —
  // never a cursor-rate × elapsed-days pro-rata. WTD used to read
  // `actualStpd × REPORT_DAILY.wtdDaysInWeek` with that seed field at 1, so the
  // row printed the open 08 Jun day as the whole week; it now names its window
  // and its plan the way the MTD row beneath it does.
  return {
    wtd: `${formatDst(wtd.tonnesDst)} (${wtd.windowLabel} · ${wtd.dayCount} complete production days) vs ${formatDst(wtd.planDst)} plan`,
    mtd: `${formatDst(mtd.tonnesDst)} (${mtd.windowLabel} · ${mtd.dayCount} complete production days) vs ${formatDst(mtd.planDst)} plan`,
  };
}

function forecastById(id: string) {
  return FORECASTS.find((row) => row.id === id);
}

function reconciliationRowValues(rowId: string): readonly string[] {
  const row = RECONCILIATION_CHECK.rows.find((entry) => entry.id === rowId);
  if (!row) return [];
  return row.values;
}

const METAL_ACCOUNT_NODES = new Set(['sp-crush', 'sp-grind', 'sp-pb', 'sp-zn', 'sp-filter']);

function subprocessLabel(nodeId: string): string {
  return SUBPROCESSES.find((row) => row.id === nodeId)?.name ?? nodeId;
}

export function dailyAccountingReportView(): DailyAccountingReportView {
  const report = ACCOUNTING_REPORT;
  const closures = allClosureReads(periodView('day')).filter((read) => METAL_ACCOUNT_NODES.has(read.nodeId));
  const metalIn = reconciliationRowValues('rcr-metal-in');
  const concMass = reconciliationRowValues('rcr-conc-mass');

  // r10 MUST-FIX 1 — the circuit-gap rows read from the same day view the
  // balance-by-node rows do, sliced by subprocess, so the two regions agree
  // by construction and the seed's `rcr-gap` design-basis figures are never
  // printed beside engine output.
  const gapFor = (nodeId: string): DailyLabelValueRow => {
    const read = closures.find((entry) => entry.nodeId === nodeId);
    return {
      label: `${read?.element ?? (nodeId === 'sp-zn' ? 'Zn' : 'Pb')} circuit gap`,
      value: read && read.closurePct !== null ? formatGapPct(read.closurePct) : 'unmeasured',
      muted: !read || read.tone === 'unmeasured',
    };
  };

  return {
    // Show, don't tell (r11 SHOULD-FIX 1/2): the reader sees which rows are
    // design basis from the muted-value channel, the same one `unmeasured`
    // uses — the card carries a decode, not a sentence about itself. "Figure",
    // not "value": `unmeasured` is also muted but names itself, so only a
    // muted *number* is the design-basis tell.
    designBasisScope: 'Muted figure = design basis',
    provenance: report.provenance,
    productionSummary: [
      { label: 'Feed', value: `${report.lines[0]?.cells[0]?.text ?? 'n/d'}`, muted: true },
      {
        label: 'Metal in feed',
        value: metalIn.length >= 2 ? `${metalIn[0]} · ${metalIn[1]}` : 'n/d',
        muted: true,
      },
      {
        label: 'Concentrate mass',
        value: concMass.length >= 2 ? `${concMass[0]} · ${concMass[1]}` : 'n/d',
        muted: true,
      },
    ],
    balanceByNode: closures.map((read) => ({
      label: subprocessLabel(read.nodeId),
      value:
        read.closurePct !== null
          ? `${read.element} · ${formatGapPct(read.closurePct)} gap`
          : 'unmeasured',
      muted: read.tone === 'unmeasured',
    })),
    reconciliationAdjustments: [
      gapFor('sp-zn'),
      gapFor('sp-pb'),
      {
        label: 'Pb concentrate (reconciled)',
        value: report.lines.find((line) => line.id === 'al-metal-conc')?.cells[0]?.text ?? 'n/d',
        muted: true,
      },
    ],
    inventory: report.lines
      .filter((line) => line.id === 'al-inventory')
      .flatMap((line) =>
        line.cells.map((cell, index) => ({
          label: line.label,
          value: `${report.circuits[index] ?? 'circuit'} · ${cell.text}`,
          muted: true,
        })),
      ),
    signOff: 'none',
  };
}

export function reportTemplate(tab: ReportsTab): ReportTemplate | undefined {
  return REPORT_TEMPLATES.find((row) => row.id === TEMPLATE_BY_TAB[tab]);
}

export function templateHeaderLine(tab: ReportsTab): string | undefined {
  const template = reportTemplate(tab);
  if (!template) return undefined;
  return `${template.audienceRole.join(', ')} · ${template.cadence} · ${formatChannel(template.channel)}`;
}

function workflowChipForTemplate(template: ReportTemplate): { text: string; title: string } {
  if (!template.workflowId) return { text: '', title: '' };
  return { text: templateShortName(template), title: template.workflowId };
}

export function workflowTabChip(tab: ReportsTab): string {
  const template = reportTemplate(tab);
  if (!template?.workflowId) return '';
  return templateShortName(template);
}

export function workflowTabChipTitle(tab: ReportsTab): string {
  const template = reportTemplate(tab);
  return template?.workflowId ?? '';
}

export function workflowMeta(tab: ReportsTab): string {
  const template = reportTemplate(tab);
  if (!template?.workflowId) return '';
  const instance = REPORT_INSTANCES.find((row) => row.templateId === template.id);
  if (!instance) return '';
  const parts = [`Last run ${instance.generatedAt}`];
  if (template.nextSend) {
    parts.push(`next send ${template.nextSend}`);
  }
  return parts.join(' · ');
}

export function workflowProvenanceLine(tab: ReportsTab): string {
  const template = reportTemplate(tab);
  if (!template?.workflowId) return '';
  return `Generated by workflow ${template.workflowId}`;
}

export function templateRegistryRows(showMoney = true): TemplateRegistryRow[] {
  return REPORT_TEMPLATES.filter((template) => showMoney || !reportTemplateCarriesMoney(template)).map((template) => {
    const instance = REPORT_INSTANCES.find((row) => row.templateId === template.id);
    const evaluatedChip = instance
      ? formatEvaluatedAgainstChip(instance.evaluatedAgainst)
      : 'n/d';
    const lastInstance = instance
      ? instance.deliveredAt
        ? `${instance.generatedAt} · sent ${instance.deliveredAt}`
        : instance.generatedAt
      : 'n/d';
    const workflow = workflowChipForTemplate(template);
    return {
      tabId: TAB_BY_TEMPLATE[template.id] ?? 'daily-management',
      templateId: template.id,
      title: template.title,
      audience: template.audienceRole.join(', '),
      cadence: template.cadence,
      channel: formatChannel(template.channel),
      lastInstance,
      workflowChip: workflow.text,
      workflowChipTitle: workflow.title,
      evaluatedAgainstChip: evaluatedChip,
    };
  });
}

export function dailyManagementReportView(
  projectSlug: string,
  state: SessionState,
  advisory: AdvisorySessionState,
  editionCutoff: SeriesTime = state.cursor,
  showMoney = true,
): DailyManagementReportView {
  const template = reportTemplate('daily-management');
  const ore = kpiTriple('kpi-ore-tpd');
  const znRec = kpiTriple('kpi-zn-rec');
  const pbRec = kpiTriple('kpi-pb-rec');
  const znGrade = kpiTriple('kpi-zn-grade');
  const pbConc = ROLE_KPIS.find((row) => row.id === 'met-pb-conc');
  const agConc = kpiTriple('kpi-ag-grade');
  const znRecoveryCase = CASES.find((row) => row.id === 'case-zn-recovery');
  const asTrendCase = CASES.find((row) => row.id === 'case-as-trend');
  const asInZnConc = ROLE_KPIS.find((row) => row.id === 'as-zn');
  const asInRomOre = ROLE_KPIS.find((row) => row.id === 'as-rom');
  const moneyPathCase = znRecoveryCase;
  const activeP1 = ALARMS.some((row) => row.priority === 'P1' && row.state === 'active');
  const metalIn = reconciliationRowValues('rcr-metal-in');
  const concMass = reconciliationRowValues('rcr-conc-mass');
  const oreBasisWord = (ore.basisLabel ?? BASIS_LABEL.budget).toLowerCase();
  const throughputPct =
    ore.actual !== undefined && ore.basisTarget
      ? `${((ore.actual / ore.basisTarget) * 100).toFixed(1)}% of ${oreBasisWord} target`
      : 'n/d';

  const executiveBullets: DailyExecutiveBullet[] = [
    {
      text: `Ore throughput ${throughputPct} (${ore.actual !== undefined ? formatTonnes(ore.actual) : 'n/d'} vs ${ore.basisTarget !== undefined ? formatTonnes(ore.basisTarget) : 'n/d'}).`,
      sectionId: 'daily-report-production',
    },
    {
      text: `Zn circuit: ${znRecoveryExecutiveBullet(state)}`,
      sectionId: 'daily-report-recovery',
    },
    {
      text: `Pb circuit: Recovery (24h avg) ${pbRec.actual !== undefined ? formatPercent(pbRec.actual) : 'n/d'} vs ${(pbRec.basisLabel ?? BASIS_LABEL.budget).toLowerCase()} ${pbRec.basisTarget !== undefined ? formatPercent(pbRec.basisTarget) : 'n/d'}.`,
      sectionId: 'daily-report-recovery',
    },
    {
      text: `Silver (Ag in Pb/Ag conc): ${agConc.actual !== undefined ? `${agConc.actual} oz/t` : 'n/d'} vs design ${agConc.designTarget !== undefined ? `${agConc.designTarget}` : 'n/d'}${agConc.deltaVsDesign ? ` (${agConc.deltaVsDesign})` : ''}.`,
      sectionId: 'daily-report-recovery',
    },
    {
      text: activeP1
        ? 'Material production interruptions flagged on active P1 alarms.'
        : 'No material production interruptions.',
      sectionId: 'daily-report-stability',
    },
  ];

  const pinnedCaseIds = effectiveReportPinnedCaseIds(advisory);
  const daysBelowDesign = znDaysBelowDesign(state);
  const dayLabel = daysBelowDesign === 1 ? 'day' : 'days';
  const trackedIssues: DailyTrackedIssueView[] = pinnedCaseIds
    .map((id) => {
      const pinnedCase = CASES.find((row) => row.id === id);
      if (!pinnedCase) return null;
      const limeAction = applyRecommendedActions(RECOMMENDED_ACTIONS, advisory).find(
        (row) => row.caseId === id && row.status === 'done',
      );
      const latestDecision = limeAction?.text ?? pinnedCase.causeCandidate ?? pinnedCase.title;
      return {
        caseId: id,
        title: displayCaseTitle(pinnedCase, state),
        recoveryLine: `${daysBelowDesign} ${dayLabel} below design`,
        latestDecision,
        drawerHref: `${projectSidebarHref(projectSlug, 'advisory')}?case=${id}`,
      };
    })
    .filter((row): row is DailyTrackedIssueView => row !== null);

  const cases = applyCases(CASES, state);
  const emergingRisks = cases
    .filter(
      (row) =>
        !RECOVERY_CASE_IDS.has(row.id) &&
        !row.resolvedAt &&
        (EMERGING_RISK_CASE_IDS.has(row.id) || row.kind === 'hardness'),
    )
    .map((row) => row.title);

  const oreRollups =
    ore.actual !== undefined && ore.basisValue !== undefined
      ? oreThroughputRollups(juneWtdTonnes(state), juneMtdTonnes(state))
      : null;
  const zn7DayForecast = forecastById('fc-w24-zn-rec');
  const pb7DayForecast = forecastById('fc-w24-pb-rec');
  const metal30DayForecast = forecastById('fc-m4-metal');
  const millAvailability = REPORT_DAILY.millAvailabilityPct;

  const instance = REPORT_INSTANCES.find((row) => row.templateId === template?.id);
  const comparisonBasisLabel = resolvePageComparisonBasis(DAILY_REPORT_KPI_IDS);
  const basisWord = comparisonBasisLabel ?? BASIS_LABEL.budget;

  return {
    workflowTabChip: workflowTabChip('daily-management'),
    workflowTabChipTitle: workflowTabChipTitle('daily-management'),
    workflowMeta: workflowMeta('daily-management'),
    workflowProvenanceLine: workflowProvenanceLine('daily-management'),
    evaluatedAgainstChip: instance
      ? formatEvaluatedAgainstChip(instance.evaluatedAgainst)
      : 'n/d',
    comparisonBasisLabel,
    executiveBullets,
    trackedIssues,
    aiSummary: (() => {
      if (!moneyPathCase) return 'n/d';
      const limeAction = applyRecommendedActions(RECOMMENDED_ACTIONS, advisory).find(
        (row) => row.id === 'ra-2',
      );
      if (!limeAction) return moneyPathCase.causeCandidate;
      const diagnosis = diagnose(state, advisory);
      const eff = effectivenessForAction(
        limeAction,
        moneyPathCase,
        state,
        advisory,
        diagnosis,
        RECOMMENDED_ACTIONS,
      );
      const response =
        eff && eff.state !== 'awaiting-action'
          ? eff.sentence.split('·').slice(-2).join('·').trim()
          : 'Zn float pH held · no recovery response since 08:00';
      return `${moneyPathCase.causeCandidate} · ${response}`;
    })(),
    productionRows: [
      {
        label: 'Ore processed (today)',
        value:
          ore.actual !== undefined && ore.basisValue !== undefined
            ? `${formatTonnes(ore.actual)} vs ${basisWord} ${formatTonnes(ore.basisValue)} (${ore.deltaVsTarget ?? 'n/d'})`
            : 'n/d',
      },
      {
        label: 'Ore processed (week-to-date)',
        value: oreRollups?.wtd ?? 'n/d',
      },
      {
        label: 'Ore processed (month-to-date)',
        value: oreRollups?.mtd ?? 'n/d',
      },
      {
        label: 'Zn concentrate grade',
        value: znGrade.actual !== undefined ? `${formatPercent(znGrade.actual)} Zn` : 'n/d',
      },
      {
        label: 'Pb concentrate grade',
        value: pbConc?.value ?? 'n/d',
      },
      {
        label: 'Ag in Pb/Ag concentrate',
        value: agConc.actual !== undefined ? `${agConc.actual} oz/t` : 'n/d',
      },
      {
        label: 'Concentrate tonnage (today)',
        value:
          concMass.length >= 2 ? `${concMass[0]} · ${concMass[1]}` : 'n/d',
      },
      {
        label: 'Metal units (today)',
        value: metalIn.length >= 2 ? `${metalIn[0]} · ${metalIn[1]}` : 'n/d',
      },
    ],
    recoveryRows: [
      {
        metal: 'Zn recovery',
        actual: znRec.actual !== undefined ? formatPercent(znRec.actual) : 'n/d',
        basisLabel: znRec.basisLabel ?? comparisonBasisLabel ?? BASIS_LABEL.budget,
        target:
          znRec.basisValue !== undefined ? formatPercent(znRec.basisValue) : 'n/d',
        designSecondary: znRec.designSecondary,
        aiInsight: znRecoveryCase?.causeCandidate,
      },
      {
        metal: 'Pb recovery',
        actual: pbRec.actual !== undefined ? formatPercent(pbRec.actual) : 'n/d',
        basisLabel: pbRec.basisLabel ?? comparisonBasisLabel ?? BASIS_LABEL.budget,
        target:
          pbRec.basisValue !== undefined ? formatPercent(pbRec.basisValue) : 'n/d',
        designSecondary: pbRec.designSecondary,
      },
    ],
    impurityRows: [
      {
        label: 'As in Pb/Ag concentrate',
        value: asTrendCase
          ? '0.32% vs 0.50% smelter limit · ~55 days to penalty at current rate'
          : 'n/d',
      },
      { label: 'As in Zn concentrate', value: asInZnConc?.value ?? 'n/d' },
      { label: 'As in ROM ore', value: asInRomOre?.value ?? 'n/d' },
    ],
    financialBasisChip: showMoney ? financialSnapshot(state).basisLabel : '',
    financialBasisProvenance: showMoney ? financialSnapshot(state).basisProvenance : '',
    financialRows: showMoney ? financialSnapshot(state).rows : [],
    availability: `${formatPercent(millAvailability.actual)} vs ${formatPercent(millAvailability.target)}`,
    emergingRisks,
    environmentalRows: environmentalComplianceRows(),
    forecastingLines: [
      {
        label: '7-day recovery',
        value: `Zn ${zn7DayForecast?.value ?? 'n/d'} · Pb ${pb7DayForecast?.value ?? 'n/d'}`,
      },
      {
        label: '30-day metal production',
        value: metal30DayForecast?.value ?? 'n/d',
      },
    ],
    forecastingProvenance: {
      label: 'Latest estimate in Planning',
      href: `/projects/${projectSlug}/planning`,
    },
    recommendedActions: (() => {
      const actions = applyRecommendedActions(RECOMMENDED_ACTIONS, advisory).filter(
        (row) => row.sequence !== null,
      );
      const dispositionFor = (action: RecommendedAction): { label: string; undecided: boolean } => {
        if (
          action.decision &&
          !decisionAtOrBeforeCutoff(asCaseTime(action.decision.at), editionCutoff)
        ) {
          return { label: 'awaiting decision', undecided: true };
        }
        if (!action.decision) return { label: 'awaiting decision', undecided: true };
        if (action.status === 'rejected') {
          return { label: `rejected · ${action.decision.reason ?? 'no reason'}`, undecided: false };
        }
        if (action.status === 'done') {
          const who = seatLabelById(action.decision.seat);
          const mins = action.implementedAt
            ? diffMinutes(asCaseTime(action.implementedAt), asCaseTime(action.decision.at))
            : undefined;
          const elapsed = mins !== undefined && mins >= 0 ? `${mins} m` : '—';
          return { label: `implemented · ${who} · ${elapsed}`, undecided: false };
        }
        if (action.status === 'approved') {
          return { label: 'approved · not yet implemented', undecided: false };
        }
        return { label: 'awaiting decision', undecided: true };
      };
      return actions
        .map((row) => {
          const { label, undecided } = dispositionFor(row);
          return {
            actionId: row.id,
            text: row.text,
            urgency: row.urgency,
            disposition: label,
            undecided,
          };
        })
        .sort((a, b) => {
          if (a.undecided !== b.undecided) return a.undecided ? -1 : 1;
          return a.actionId.localeCompare(b.actionId);
        });
    })(),
    shiftActionGroups: dailyShiftActionGroups(state, advisory, editionCutoff),
    editions: [
      {
        id: 'sent',
        label: '08 Jun 08:00 edition · sent 08:02 · cutoff 08:00',
        cutoff: '2026-06-08T08:00' as SeriesTime,
        editBeforeSend: false,
        editDisabledReason: 'Sent edition — edit before send is closed',
      },
      {
        id: 'current',
        label: `current · to ${formatClockShort(state.cursor).split('·').pop()?.trim() ?? '11:30'}`,
        cutoff: state.cursor,
        editBeforeSend: true,
      },
    ],
    activeEditionId: 'current',
    decisionWindowLabel: 'Decision window: trailing 24 h to edition cutoff',
    paginateBySection: true,
    guidedLinks: [
      { label: 'Superintendent report', href: `/projects/${projectSlug}/overview?role=superintendent` },
      { label: 'Supervisor / Crew', href: `/projects/${projectSlug}/compliance?tab=crew` },
      { label: 'Metallurgist technical', href: '', tab: 'shift-report' },
      { label: 'AI insights', href: `/projects/${projectSlug}/advisory` },
      { label: 'Circuit-by-circuit', href: `/projects/${projectSlug}/plant-model` },
    ],
  };
}

export function weeklyPeriodHeader(plantName: string, cursor: SeriesTime = DEFAULT_CURSOR): string {
  const weekEnd = formatClock(cursor).split('·')[0]?.trim() ?? '08 Jun 2026';
  const comparisonBasisLabel = resolvePageComparisonBasis(DAILY_REPORT_KPI_IDS);
  return `${plantName} · week to ${weekEnd}${comparisonBasisLabel ? ` · comparison basis: ${comparisonBasisLabel}` : ''}`;
}

export function weeklySummaryTable(): RoleTable | undefined {
  return ALL_TABLES.find(
    (table) => table.roleId === 'gm' && table.tabId === 'weekly',
  );
}

/** The plant week's complete production days — `juneWtdDays`, the same six days
 *  `/meeting`, the Overview and Metal accounting's `wtdWindow()` read (K21 /
 *  D25, #719). `to` is the open production day's stamp, so a mean bounded by it
 *  covers exactly the days the tonnes row sums. */
function weeklyWindow(state: SessionState): { from: SeriesTime; to?: SeriesTime } {
  const days = juneWtdDays(state);
  return { from: weekStart(state.cursor), to: days[days.length - 1]?.closedAt };
}

/** Mean of a series' measured points in the plant week's closed window
 *  `[from, to)` — the bounded twin of `windowMean`, which runs to the cursor
 *  and would pull in the open 08 Jun production day. `null` when the window
 *  carries no measured point, so a caller drops the row rather than rendering
 *  absence (`product-vision.md` § Absence is not content). */
function weeklyWindowMean(seriesId: string, state: SessionState): number | null {
  const { from, to } = weeklyWindow(state);
  if (!to) return null;
  const points = seriesAt(seriesId, state).points.filter(
    (point): point is { at: SeriesTime; value: number } =>
      point.value !== null && diffMinutes(point.at, from) >= 0 && diffMinutes(point.at, to) < 0,
  );
  if (points.length === 0) return null;
  return points.reduce((sum, point) => sum + point.value, 0) / points.length;
}

/** `<recovery>% / <grade>% Pb` — the measured week's recovery and concentrate
 *  grade, the pair the row's Design (PFS) cell names. Recovery is the
 *  accounting engine's WTD read (`recoveryActualPct` over `periodView('wtd')`),
 *  the same figure `/metal-accounting`'s Recovery performance WTD row and the
 *  daily report show; the grade is the week's mean of its own shift series
 *  (`rs-*-grade`), the read `/meeting`'s WTD column and the Overview's by-area
 *  WTD column print (K21 / #719). `null` when either figure has no complete day
 *  behind it. */
function weeklyRecoveryGradeCell(metal: 'Pb' | 'Zn', state: SessionState): string | null {
  const rec = recoveryActualPct(metal, periodView('wtd'));
  const grade = weeklyWindowMean(metal === 'Pb' ? 'rs-pb-grade' : 'rs-zn-grade', state);
  if (rec === null || grade === null) return null;
  return `${rec.toFixed(1)}% / ${grade.toFixed(1)}% ${metal}`;
}

/** Rollup rows exclude the placeholder advisories line — rendered as its own region.
 *
 *  K21 round 2 (#719) / D25: the tonnes row's Actual (WTD) cell is the shared
 *  week window (`juneWtdTonnes`) — production days 2–7 Jun, 10,652 dst over the
 *  6 complete days — not the seed's `pending historian`, which rendered absence
 *  as content and left the one WTD figure off the row. The Design (PFS) cell
 *  keeps the row's own 1,800 stpd basis, scaled to the same 6 days (10,800 dst),
 *  so comparison and actual cover one window and the unit is the same `dst`.
 *
 *  #739: the Pb and Zn recovery / grade rows read the same plant week — the
 *  engine's WTD recovery and the week's mean concentrate grade — where the seed
 *  still carried `pending historian`. A row whose week has no measured figure is
 *  not drawn; absence is never content. */
export function weeklyMetricRows(table: RoleTable, state: SessionState): RoleTable {
  const wtd = juneWtdTonnes(state);
  const designStpd = DESIGN_THROUGHPUT_STPD.toLocaleString('en-US');
  const recoveryCells = new Map<string, string>();
  const droppedIds = new Set<string>();
  const pbCell = weeklyRecoveryGradeCell('Pb', state);
  const znCell = weeklyRecoveryGradeCell('Zn', state);
  if (pbCell === null) droppedIds.add('gm-w2');
  else recoveryCells.set('gm-w2', pbCell);
  if (znCell === null) droppedIds.add('gm-w3');
  else recoveryCells.set('gm-w3', znCell);
  return {
    ...table,
    rows: table.rows
      .filter((row) => row.id !== 'gm-w6' && !droppedIds.has(row.id))
      .map((row) => {
        if (row.id === 'gm-w1') {
          return {
            ...row,
            cells: [
              row.cells[0] ?? 'Tonnes processed',
              `${(wtd.dayCount * DESIGN_THROUGHPUT_STPD).toLocaleString('en-US')} dst (${wtd.dayCount} × ${designStpd} stpd)`,
              `${wtd.tonnesDst.toLocaleString('en-US')} dst (${wtd.windowLabel} · ${wtd.dayCount} complete production days)`,
            ],
          };
        }
        const cell = recoveryCells.get(row.id);
        return cell === undefined
          ? row
          : { ...row, cells: [row.cells[0]!, row.cells[1]!, cell] };
      }),
  };
}

/** Human-facing weekly metrics subtitle — strips doc citations from seed caption. */
export function weeklySummarySubtitle(table: RoleTable): string {
  if (table.caption.includes('Weekly summary')) {
    return 'Design-basis metrics for the week';
  }
  return table.caption;
}

// ---------------------------------------------------------------------------
// Met shift report cells (ui-lift.C40 #437) — every figure-bearing cell reads
// the seed the other surfaces read, never a retyped literal. The two retired
// literals (`actual pending historian`, `Pb circuit flagged — … 112%`) sat
// under a design-basis rule that is still closed: `pending` is on the closed
// absence list (`product-vision.md` § Absence is not content) and the Pb flag
// was closed by `vision.V2` #307. Round 2: the circuit recovery / grade row
// reads the measured `rd-kpi-*-rec` / `rd-kpi-*-grade` readings against their
// design (D13), not the design recoveries (`rcr-design-rec`) as if measured,
// and the reconciliation row reads the measured closure gap `/metal-accounting`
// and the daily accounting report show. `reconciliationRowValues` stays the
// same read the daily reports use for the design-document rows.
// ---------------------------------------------------------------------------

/** Measured fresh feed + head grades at the demo cursor. WIT-021 reads
 *  `rd-unit-bm01-feed` (1,720 st/d ÷ 24 = 71.7 stph vs 75.0 design); the head
 *  grades read their own `rd-head-*` readings — the same values the plant
 *  model and the daily meeting read. */
function shiftFeedCell(): string {
  const feed = readingByIdLookup('rd-unit-bm01-feed');
  const pb = readingByIdLookup('rd-head-pb');
  const zn = readingByIdLookup('rd-head-zn');
  if (!feed || !pb || !zn) return 'n/d';
  const actual = formatValue(feed);
  const design = formatValue(feed, { withUnit: true, value: feed.design });
  return `WIT-021 ${actual} vs ${design} design · ${formatValue(pb, { withUnit: true })} · ${formatValue(zn, { withUnit: true })}`;
}

/** Per-metal recovery and concentrate grade, each read from that metal's own
 *  `rd-kpi-*-rec` / `rd-kpi-*-grade` readings — the same Actual vs Design pair
 *  `/metal-accounting`'s Recovery performance table shows, in its Zn · Pb · Ag
 *  order (the `RECONCILIATION_CHECK` column order). D13: the measured reading is
 *  the plant's one current recovery, so no design recovery (`rcr-design-rec`) is
 *  printed as the shift's figure. The measured concentrate grade is named as
 *  measured so the row cannot be read as the design-basis grade
 *  (`rd-kpi-*-grade` carries both; the seed's design grades are `rcr-conc-grade`). */
function shiftRecoveryGradeCell(): string {
  const metals = [
    { metal: 'Zn', recId: 'rd-kpi-zn-rec', gradeId: 'rd-kpi-zn-grade' },
    { metal: 'Pb', recId: 'rd-kpi-pb-rec', gradeId: 'rd-kpi-pb-grade' },
    { metal: 'Ag', recId: 'rd-kpi-ag-rec', gradeId: 'rd-kpi-ag-grade' },
  ] as const;
  return metals
    .map(({ metal, recId, gradeId }) => {
      const rec = readingByIdLookup(recId);
      const grade = readingByIdLookup(gradeId);
      if (!rec || !grade) return `${metal} n/d`;
      const actual = formatValue(rec, { withUnit: true });
      const design = formatValue(rec, { withUnit: true, value: rec.design });
      const gradeText = formatValue(grade, { withUnit: true });
      const labelledGrade = gradeText.includes(metal) ? gradeText : `${gradeText} ${metal}`;
      return `${metal} ${actual} vs ${design} design · ${labelledGrade} con (measured)`;
    })
    .join(' · ');
}

/** The Pb circuit's measured closure gap at the cursor — the same day read
 *  `/metal-accounting`'s graph chip and Balance tab and the daily accounting
 *  report's reconciliation-adjustment row use
 *  (`closureRead('sp-pb', periodView('day'))`), replacing round 1's
 *  design-document path gap (`rcr-gap`). Not the retired 112 % flag. */
function shiftReconciliationCell(): string {
  const read = closureRead('sp-pb', periodView('day'));
  return `Pb circuit closure ${read.closurePct !== null ? formatGapPct(read.closurePct) : 'unmeasured'}`;
}

/** Cyclones in service off `rd-c-cyc-n` (4 of 5 installed) — the same reading
 *  the plant bottleneck read uses. */
function shiftBottleneckCell(): string {
  const cyclones = readingByIdLookup('rd-c-cyc-n');
  const inService = cyclones?.value ?? 'n/d';
  const installed = cyclones?.dcsRange?.hi ?? 'n/d';
  return `Grinding (mill + cyclones) tightest on design margin; ${inService} of ${installed} cyclones in service`;
}

/** The two outstanding evidence requests read their own seed status — an open
 *  request is `open`; `pending` never returns to the glass. */
function shiftOpenCasesCell(): string {
  const cuso4Case = CASES.find((row) => row.id === 'case-cuso4');
  const titration = cuso4Case?.evidenceRequests.find((row) => row.id === 'ev-cuso4-titration');
  return `Zn recovery — CuSO4 titration ${titration?.status ?? 'open'} · tramp iron visual on CV-001`;
}

/** Sample pipeline off `SAMPLE_POINTS`: the id range plus every non-REG
 *  adherence (`SA05 LATE · SA06 MISS`). */
function shiftSamplePipelineCell(): string {
  const first = SAMPLE_POINTS[0]?.id ?? 'SA01';
  const last = SAMPLE_POINTS[SAMPLE_POINTS.length - 1]?.id ?? 'SA08';
  const exceptions = SAMPLE_POINTS.filter((point) => point.adherence !== 'REG').map(
    (point) => `${point.id} ${point.adherence}`,
  );
  return [`${first}–${last}`, ...exceptions].join(' · ');
}

/** The one seeded control-loop exception off `CONTROL_LOOPS` (`loop-lic4010`);
 *  the mode word and the dwell both come from the loop record. */
function shiftControlLoopCell(): string {
  const loop = CONTROL_LOOPS.find((row) => row.id === 'loop-lic4010');
  if (!loop) return 'n/d';
  const mode = loop.mode.charAt(0).toUpperCase() + loop.mode.slice(1);
  return `${loop.tag} ${mode} ${loop.duration} — Pb rougher cell 1. See Control loops table.`;
}

function shiftReportCell(rowId: string): string | undefined {
  switch (rowId) {
    case 'met-s1':
      return shiftFeedCell();
    case 'met-s2':
      return shiftRecoveryGradeCell();
    case 'met-s3':
      return shiftReconciliationCell();
    case 'met-s4':
      return shiftBottleneckCell();
    case 'met-s5':
      return shiftOpenCasesCell();
    case 'met-s6':
      return shiftSamplePipelineCell();
    case 'met-s7':
      return shiftControlLoopCell();
    default:
      return undefined;
  }
}

/** The Met shift-report RoleTable: the seed owns the section names and their
 *  order (M4 §4.1); every content cell is read from the seed above. */
export function shiftReportTables(): RoleTable[] {
  return ALL_TABLES.filter(
    (table) => table.roleId === 'metallurgist' && table.tabId === 'shift-report',
  ).map((table) => ({
    ...table,
    rows: table.rows.map((row) => {
      const derived = shiftReportCell(row.id);
      return derived === undefined ? row : { ...row, cells: [row.cells[0]!, derived] };
    }),
  }));
}

/** Advisories this week — CASES counted by state (M4 static screen). */
export function advisoriesByState(): AdvisoryStateCount[] {
  const counts = new Map<CaseState, number>();
  for (const caseItem of CASES) {
    counts.set(caseItem.state, (counts.get(caseItem.state) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([state, count]) => ({
      state,
      label: STATE_CHIP[state].label,
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}
